import React, { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { CreatureObject } from '@common/FabulaUltimaGame';
import { Api } from '@common/FabulaUltimaGame';
import './GameStateButton.css';

interface GameStateButtonProps {
  characters: CreatureObject[];
  enemies: CreatureObject[];
  commandHistory: Api.UploadGameStateCommandHistory[];
  onUploadGameState: (req: Api.UploadGameStateRequest) => void;
  canUpload: boolean;
}

function serializeCreature(c: CreatureObject): Api.UploadGameStateCreature {
  return {
    name: c.name,
    kind: c.kind,
    hp: c.hp,
    maxHp: c.maxHp,
    mp: c.mp,
    maxMp: c.maxMp,
    ip: c.ip,
    maxIp: c.maxIp,
    dex: c.dex,
    ins: c.ins,
    mig: c.mig,
    wlp: c.wlp,
    physicalDefenseModifier: c.physicalDefenseModifier,
    magicDefenseModifier: c.magicDefenseModifier,
    fixedPhysicalDefense: c.fixedPhysicalDefense,
    studyLevel: c.studyLevel,
    statusEffects: [...c.statusEffects],
    buffs: [...c.buffs],
  };
}

export const GameStateButton: React.FC<GameStateButtonProps> = ({
  characters,
  enemies,
  commandHistory,
  onUploadGameState,
  canUpload,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownload = () => {
    const data: Api.GameStateSaveData = {
      version: Api.kUploadGameStateVersion,
      creatures: [...characters, ...enemies].map(serializeCreature),
      commandHistory,
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fu-game-state-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (!data.creatures || !Array.isArray(data.creatures)) {
          alert('Invalid game state file: missing creatures array');
          return;
        }
        onUploadGameState({
          version: data.version ?? 0,
          creatures: data.creatures,
          commandHistory: data.commandHistory ?? [],
        });
      } catch (err) {
        alert('Failed to parse game state file');
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be re-uploaded
    e.target.value = '';
  };

  return (
    <>
      <Button
        variant="success"
        size="lg"
        className="game-state-download-button"
        onClick={handleDownload}
        title="下载当前游戏状态"
      >
        ↓
      </Button>
      {canUpload && (
        <>
          <Button
            variant="warning"
            size="lg"
            className="game-state-upload-button"
            onClick={handleUpload}
            title="上传游戏状态"
          >
            ↑
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </>
      )}
    </>
  );
};
